// Gestion des backups CSV des paniers.
// Stockage principal : dossier serveur en ligne (/csv/) via l'API PHP.
// Secours : localStorage du navigateur si le serveur est injoignable.

import axios from 'axios';

const STORAGE_KEY = 'lotismode_csv_backups';
const MAX_BACKUPS = 50;

// Base de l'API PHP (même origine que l'app, ex: https://live.lotismode.com/php/backup_api)
function getCsvApiBase(): string {
  return `${window.location.origin}/php/backup_api`;
}

// Génère un nom de fichier horodaté
function makeFilename(): string {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
  return `paniers-live-${stamp}.csv`;
}

// --- Backups en ligne (dossier /csv/ sur le serveur) ---

export interface ServerCsvBackup {
  name: string;
  size: string;
  date: string; // 'Y-m-d H:i:s'
  url: string;
}

// Enregistre un backup CSV dans le dossier /csv/ en ligne
export async function saveCsvBackupServer(baskets: any[]): Promise<{ success: boolean; filename: string; url: string }> {
  const content = buildBasketsCsv(baskets);
  const filename = makeFilename();

  const formData = new FormData();
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  formData.append('file', blob, filename);

  const res = await axios.post(`${getCsvApiBase()}/save_csv.php`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });

  if (!res.data || !res.data.success) {
    throw new Error(res.data?.message || 'Échec de l\'enregistrement du backup CSV en ligne');
  }
  return res.data;
}

// Liste les backups CSV présents dans le dossier /csv/ en ligne
export async function listCsvBackupsServer(): Promise<ServerCsvBackup[]> {
  const res = await axios.get(`${getCsvApiBase()}/list_csv.php`, { timeout: 20000 });
  return Array.isArray(res.data) ? res.data : [];
}

// Supprime un backup CSV en ligne
export async function deleteCsvBackupServer(filename: string): Promise<void> {
  const res = await axios.post(`${getCsvApiBase()}/delete_csv.php`, { filename }, { timeout: 15000 });
  if (res.data && res.data.success === false) {
    throw new Error(res.data.message || 'Échec de la suppression');
  }
}

export interface CsvBackup {
  id: string;
  filename: string;
  date: string;   // ISO
  count: number;  // nombre de paniers
  content: string; // contenu CSV complet
}

// Échappement CSV : entoure de guillemets et double les guillemets internes
function esc(val: any): string {
  return `"${String(val ?? '').replace(/"/g, '""')}"`;
}

// Construit le contenu CSV à partir des paniers
export function buildBasketsCsv(baskets: any[]): string {
  const rows = (baskets || []).map((p) => {
    let items = '';
    try {
      const parsed = typeof p.items === 'string' ? JSON.parse(p.items) : p.items;
      items = Array.isArray(parsed) ? parsed.join(' | ') : String(p.items ?? '');
    } catch {
      items = String(p.items ?? '');
    }
    const date = p.created_at ? new Date(p.created_at).toLocaleString('fr-FR') : '';
    return [esc(p.client_number), esc(items), esc(p.total_price), esc(date)].join(';');
  });

  const header = ['Numéro client', 'Articles', 'Prix (€)', 'Date'].map(esc).join(';');
  // BOM UTF-8 pour que Excel affiche correctement les accents
  return '﻿' + [header, ...rows].join('\r\n');
}

// Parse un contenu CSV (séparateur ';', champs entre guillemets, BOM UTF-8) en tableau
export function parseCsv(content: string, delimiter = ';'): { headers: string[]; rows: string[][] } {
  const text = (content || '').replace(/^﻿/, ''); // retirer le BOM éventuel
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } // guillemet échappé ""
        else { inQuotes = false; }
      } else {
        field += c;
      }
    } else {
      if (c === '"') { inQuotes = true; }
      else if (c === delimiter) { record.push(field); field = ''; }
      else if (c === '\n') { record.push(field); records.push(record); record = []; field = ''; }
      else if (c === '\r') { /* ignoré, traité avec \n */ }
      else { field += c; }
    }
  }
  if (field !== '' || record.length > 0) { record.push(field); records.push(record); }

  // Retirer les lignes entièrement vides
  const nonEmpty = records.filter((r) => r.some((cell) => cell.trim() !== ''));
  const headers = nonEmpty.length > 0 ? nonEmpty[0] : [];
  const rows = nonEmpty.slice(1);
  return { headers, rows };
}

// Lit la liste des backups (du plus récent au plus ancien)
export function getCsvBackups(): CsvBackup[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as CsvBackup[];
    if (!Array.isArray(list)) return [];
    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (e) {
    console.error('Erreur lecture backups CSV:', e);
    return [];
  }
}

// Enregistre un nouveau backup CSV des paniers en cours
export function saveCsvBackup(baskets: any[]): CsvBackup {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

  const backup: CsvBackup = {
    id: `${now.getTime()}`,
    filename: `paniers-live-${stamp}.csv`,
    date: now.toISOString(),
    count: baskets?.length || 0,
    content: buildBasketsCsv(baskets),
  };

  const list = getCsvBackups();
  list.unshift(backup);
  // Conserver seulement les N plus récents
  const trimmed = list.slice(0, MAX_BACKUPS);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error('Erreur sauvegarde backup CSV (quota localStorage ?):', e);
  }

  return backup;
}

// Supprime un backup par son id
export function deleteCsvBackup(id: string): void {
  const list = getCsvBackups().filter((b) => b.id !== id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.error('Erreur suppression backup CSV:', e);
  }
}

// Supprime tous les backups
export function clearCsvBackups(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Erreur suppression des backups CSV:', e);
  }
}

// Déclenche le téléchargement d'un backup existant (action manuelle de l'utilisateur)
export function downloadCsvBackup(backup: CsvBackup): void {
  const blob = new Blob([backup.content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = backup.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
